import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  // Admin impersonation support
  adminUser: null,
  adminToken: null,
  isImpersonating: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
      // Clear impersonation on normal login
      state.isImpersonating = false;
      state.adminUser = null;
      state.adminToken = null;
      
      // Clear any impersonation backup data
      localStorage.removeItem('__admin_backup_user');
      localStorage.removeItem('__admin_backup_token');
      localStorage.removeItem('__is_impersonating');
      localStorage.removeItem('__admin_token_backup');
      localStorage.removeItem('__admin_refresh_backup');
      localStorage.removeItem('__admin_user_backup');
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    registerStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    registerSuccess: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
    },
    registerFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      // If impersonating, restore admin instead of full logout
      if (state.isImpersonating) {
        // Try Redux state first
        if (state.adminUser && state.adminToken) {
          state.user = state.adminUser;
          state.token = state.adminToken;
          state.isAuthenticated = true;
          state.isImpersonating = false;
          state.adminUser = null;
          state.adminToken = null;
          
          // Also restore admin tokens to localStorage for api.js
          localStorage.setItem('token', state.adminToken);
          localStorage.setItem('user', JSON.stringify(state.adminUser));
        }
        // Fallback to localStorage backup
        else {
          const backupUser = localStorage.getItem('__admin_backup_user');
          const backupToken = localStorage.getItem('__admin_backup_token');
          
          if (backupUser && backupToken) {
            state.user = JSON.parse(backupUser);
            state.token = backupToken;
            state.isAuthenticated = true;
            state.isImpersonating = false;
            state.adminUser = null;
            state.adminToken = null;
            
            localStorage.setItem('token', backupToken);
            localStorage.setItem('user', backupUser);
          } else {
            // No backup found - full logout
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            state.isImpersonating = false;
            state.adminUser = null;
            state.adminToken = null;
          }
        }
        
        // Clear all impersonation backup data
        localStorage.removeItem('__admin_backup_user');
        localStorage.removeItem('__admin_backup_token');
        localStorage.removeItem('__is_impersonating');
        localStorage.removeItem('__admin_token_backup');
        localStorage.removeItem('__admin_refresh_backup');
        localStorage.removeItem('__admin_user_backup');
      } else {
        // Normal logout
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isImpersonating = false;
        state.adminUser = null;
        state.adminToken = null;
      }
      state.error = null;
    },
    // Login as another user (admin impersonation)
    loginAsUser: (state, action) => {
      // Save current admin state before switching
      state.adminUser = state.user;
      state.adminToken = state.token;
      
      // Backup to localStorage in case Redux persist doesn't catch it
      if (state.user) {
        localStorage.setItem('__admin_backup_user', JSON.stringify(state.user));
      }
      if (state.token) {
        localStorage.setItem('__admin_backup_token', state.token);
      }
      localStorage.setItem('__is_impersonating', 'true');
      
      // Switch to target user
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.isImpersonating = true;
      state.loading = false;
      state.error = null;
      
      // Update localStorage tokens so api.js can read them
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
    },
    // Restore admin session manually
    restoreAdmin: (state) => {
      // Try Redux state first
      if (state.adminUser && state.adminToken) {
        state.user = state.adminUser;
        state.token = state.adminToken;
        state.isAuthenticated = true;
        state.isImpersonating = false;
        state.adminUser = null;
        state.adminToken = null;
        
        localStorage.setItem('token', state.token);
        localStorage.setItem('user', JSON.stringify(state.user));
      }
      // Fallback to localStorage backup
      else {
        const backupUser = localStorage.getItem('__admin_backup_user');
        const backupToken = localStorage.getItem('__admin_backup_token');
        
        if (backupUser && backupToken) {
          state.user = JSON.parse(backupUser);
          state.token = backupToken;
          state.isAuthenticated = true;
          state.isImpersonating = false;
          state.adminUser = null;
          state.adminToken = null;
          
          localStorage.setItem('token', backupToken);
          localStorage.setItem('user', backupUser);
        }
      }
      
      // Clear all impersonation backup data
      localStorage.removeItem('__admin_backup_user');
      localStorage.removeItem('__admin_backup_token');
      localStorage.removeItem('__is_impersonating');
      localStorage.removeItem('__admin_token_backup');
      localStorage.removeItem('__admin_refresh_backup');
      localStorage.removeItem('__admin_user_backup');
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  registerStart,
  registerSuccess,
  registerFailure,
  logout,
  loginAsUser,
  restoreAdmin,
  updateUser,
  clearError,
} = authSlice.actions;

export default authSlice.reducer;